import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, CheckCircle2, AlertCircle, ArrowRight, Mail, ShieldCheck, Landmark, Sparkles, LogIn } from "lucide-react";
import { applyEnterprise } from "@services/subscriptionService";
import { useAuth } from "@contexts/authContext";
import Button from "@components/ui/Button";
import TextInput from "@components/forms/TextInput";

export default function ApplyEnterprisePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const token = searchParams.get("token");
  
  // State: 'loading' | 'idle' | 'success' | 'error'
  const [status, setStatus] = useState(token ? "loading" : "idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm();

  // Automatic verification on mount if token exists
  useEffect(() => {
    if (token) {
      const verifyToken = async () => {
        try {
          setStatus("loading");
          await applyEnterprise({ token });
          setStatus("success");
        } catch (error) {
          console.error("Token verification failed:", error);
          const msg = error.response?.data?.detail || "This application link has expired or is invalid";
          setErrorMessage(msg);
          setStatus("error");
        }
      };
      verifyToken();
    }
  }, [token]);

  // Handle manual submission for logged-in or email-based flow
  const onSubmitApplication = async (data) => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      if (user) {
        // Authenticated user path
        await applyEnterprise({});
      } else {
        // Unauthenticated email path
        await applyEnterprise({ email: data.email });
      }
      setStatus("success");
    } catch (error) {
      console.error("Application submission failed:", error);
      const msg = error.response?.data?.detail || "Failed to submit application. Please check your credentials and try again.";
      setErrorMessage(msg);
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to reset error and return to manual application form
  const handleResetToForm = () => {
    setStatus("idle");
    setErrorMessage("");
  };

  // Animation variants for smooth state transitions
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, y: -30, transition: { duration: 0.3 } }
  };

  return (
    <div className="relative min-h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50 flex items-center justify-center py-16 px-4">
      {/* Decorative Brand Light Gradient & Blobs */}
      <div className="absolute inset-0 bg-gradient-to-tr from-blue-50/30 via-white to-violet-50/30" />
      <div className="absolute -left-1/4 top-1/4 h-96 w-96 rounded-full bg-blue-400/5 blur-[128px]" />
      <div className="absolute -right-1/4 bottom-1/4 h-96 w-96 rounded-full bg-violet-400/5 blur-[128px]" />

      <div className="relative z-10 w-full max-w-lg">
        <AnimatePresence mode="wait">
          {/* 1. LOADING STATE */}
          {status === "loading" && (
            <motion.div
              key="loading"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="card w-full bg-white border border-slate-100/80 shadow-xl p-8 text-center rounded-2xl"
            >
              <div className="flex flex-col items-center justify-center space-y-6 py-8">
                <span className="loading loading-dots loading-lg text-primary scale-125"></span>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-slate-800 tracking-wide">Verifying Application</h3>
                  <p className="text-slate-500 text-sm max-w-xs">
                    Please wait while we verify your invitation details with our servers...
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* 2. SUCCESS STATE */}
          {status === "success" && (
            <motion.div
              key="success"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="card w-full bg-white border border-slate-100/80 shadow-xl p-8 rounded-2xl overflow-hidden"
            >
              {/* Top ambient Success glow */}
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full bg-emerald-100/40 blur-3xl" />

              <div className="flex flex-col items-center text-center space-y-6 relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1, rotate: 360 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                  className="p-3 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100"
                >
                  <CheckCircle2 className="w-12 h-12" />
                </motion.div>

                <div className="space-y-3">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-800 tracking-tight">
                    Application Received!
                  </h2>
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base px-2">
                    Your application has been received! Our team is reviewing the university details, and your Enterprise account will be activated in 24 hours.
                  </p>
                </div>

                <div className="w-full pt-4 border-t border-slate-100 flex flex-col space-y-3">
                  <Link
                    to="/lecturer/dashboard"
                    className="btn btn-primary rounded-xl w-full flex items-center justify-center gap-2 group text-sm h-11"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                  {!user && (
                    <Link
                      to="/login"
                      className="btn btn-ghost border-slate-200 hover:bg-slate-100 rounded-xl w-full text-slate-600 text-sm h-11"
                    >
                      Login to your account
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* 3. ERROR STATE */}
          {status === "error" && (
            <motion.div
              key="error"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="card w-full bg-white border border-rose-100 shadow-xl p-8 rounded-2xl overflow-hidden"
            >
              {/* Top ambient Error glow */}
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full bg-rose-100/40 blur-3xl" />

              <div className="flex flex-col items-center text-center space-y-6 relative z-10">
                <div className="p-3 bg-rose-50 text-rose-600 rounded-full border border-rose-100">
                  <AlertCircle className="w-12 h-12" />
                </div>

                <div className="space-y-3 w-full">
                  <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
                    Application Failed
                  </h2>
                  <p className="text-rose-700 text-sm px-4 leading-relaxed bg-rose-50 border border-rose-100/80 py-3 rounded-xl w-full">
                    {errorMessage || "This application link has expired or is invalid"}
                  </p>
                </div>

                <div className="w-full pt-4 border-t border-slate-100 flex flex-col space-y-3">
                  <button
                    onClick={handleResetToForm}
                    className="btn btn-primary rounded-xl w-full text-sm h-11"
                  >
                    Apply Manually
                  </button>
                  <Link
                    to="/login"
                    className="btn btn-ghost border-slate-200 hover:bg-slate-100 rounded-xl w-full text-slate-600 text-sm h-11"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {/* 4. IDLE / FORM SUBMISSION STATE */}
          {status === "idle" && (
            <motion.div
              key="idle"
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="card w-full bg-white border border-slate-100/80 shadow-xl p-8 rounded-2xl overflow-hidden"
            >
              {/* Header Brand Gradient Accent line */}
              <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-blue-500 via-primary to-indigo-600" />

              <div className="space-y-6">
                {/* Logo and Icon Header */}
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-primary/5 rounded-xl border border-primary/10 text-primary">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-1.5">
                      Enterprise Application
                      <Sparkles className="w-4 h-4 text-violet-500 fill-violet-500 animate-pulse" />
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">Free institutional access program</p>
                  </div>
                </div>

                <p className="text-sm text-slate-600 leading-relaxed">
                  Join our enterprise program to equip your university or college with full-scale online evaluation and management capabilities.
                </p>

                {/* Offer features list */}
                <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Plan Features Included:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-600">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Dedicated University Dashboard</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Landmark className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Lecturer Verification Dashboard</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Building2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Classroom & Course Creation</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Detailed Student Analytics</span>
                    </div>
                  </div>
                </div>

                {user ? (
                  /* Form for Authenticated Users */
                  <form onSubmit={handleSubmit(onSubmitApplication)} className="space-y-6 pt-2">
                    <div className="bg-slate-50 border border-slate-100 p-4 rounded-xl flex items-center gap-3">
                      <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600 border border-emerald-100 shrink-0">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-slate-400">Authenticated user</p>
                        <p className="text-sm font-semibold text-slate-800 truncate">{user.email}</p>
                      </div>
                    </div>

                    <Button
                      isLoading={isSubmitting}
                      className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 group h-11"
                    >
                      {isSubmitting ? "Submitting application..." : "Apply for Enterprise"}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </form>
                ) : (
                  /* Form for Unauthenticated Users */
                  <form onSubmit={handleSubmit(onSubmitApplication)} className="space-y-5 pt-2">
                    <div className="relative">
                      <TextInput
                        label="Institution Email Address"
                        name="email"
                        type="email"
                        placeholder="lecturer@university.edu"
                        register={register}
                        errors={errors}
                        validationRules={{
                          required: "Email is required",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address format"
                          }
                        }}
                        className="mb-0"
                        inputClassName="bg-white border-slate-200 text-slate-800 focus:border-primary rounded-xl h-11 pl-4"
                      />
                    </div>

                    <Button
                      isLoading={isSubmitting}
                      className="w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 group h-11"
                    >
                      {isSubmitting ? "Submitting application..." : "Submit Application"}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>

                    <div className="flex items-center justify-center gap-2 pt-2 text-xs text-slate-500 border-t border-slate-100">
                      <span>Already have an account?</span>
                      <Link to="/login" className="text-primary hover:underline font-semibold flex items-center gap-1">
                        Log In <LogIn className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
